import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAgency } from "@/context/AgencyContext";
import { Button } from "@/components/ui/button";
import { Wallet, Landmark, CreditCard, ArrowLeft, ShieldCheck, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const METHOD_META = {
  cash: { label: "Contanti in agenzia", icon: Wallet, color: "amber" },
  bank: { label: "Bonifico bancario", icon: Landmark, color: "blue" },
  stripe: { label: "Carta di credito", icon: CreditCard, color: "indigo" },
  paypal: { label: "PayPal", icon: CreditCard, color: "sky" },
};

export default function PolicyPayment() {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { agency } = useAgency();
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/my/policy-payment-options/${policyId}`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e)); navigate("/area-riservata"); });
  }, [policyId, navigate]);

  const initiate = async (method) => {
    setSubmitting(method);
    try {
      const origin = window.location.origin;
      const { data: res } = await api.post(`/payments/initiate/${policyId}`, { method, origin_url: origin });
      if (method === "stripe") {
        window.location.href = res.url;
        return;
      }
      if (method === "paypal") {
        navigate(`/area-riservata/pagamento/paypal/${res.tx_id}`);
        return;
      }
      // cash / bank → mostro istruzioni
      setBookingResult({ ...res, method });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(null);
    }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento…</div>;

  const enabled = data.settings;
  const methods = [];
  if (enabled.cash_enabled) methods.push("cash");
  if (enabled.bank_transfer_enabled) methods.push("bank");
  if (enabled.stripe_enabled) methods.push("stripe");
  if (enabled.paypal_enabled) methods.push("paypal");

  if (bookingResult) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="payment-instructions">
        <header className="bg-[#0A192F] text-white">
          <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
            <button onClick={() => navigate("/area-riservata")} className="text-xs text-slate-300 inline-flex items-center gap-1 hover:text-white">
              <ArrowLeft className="h-3.5 w-3.5" /> Torna alla mia area
            </button>
            <span className="text-xs text-slate-400">{agency?.name || "Assicura"}</span>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 py-10">
          <div className="bg-white border border-slate-200 rounded-lg p-8">
            <div className="text-center">
              <div className="inline-flex h-14 w-14 rounded-full bg-amber-100 text-amber-700 items-center justify-center mb-4">
                {bookingResult.method === "cash" ? <Wallet className="h-7 w-7" /> : <Landmark className="h-7 w-7" />}
              </div>
              <h1 className="font-display text-2xl font-semibold">
                {bookingResult.method === "cash" ? "Pagamento prenotato in agenzia" : "Istruzioni bonifico"}
              </h1>
              <p className="text-slate-500 mt-2 text-sm">{bookingResult.instructions}</p>
            </div>

            <div className="mt-6 bg-slate-50 border border-slate-200 rounded-md p-5 space-y-3 font-mono text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">Importo</span>
                <span className="font-bold tabular-nums">{formatCurrency(bookingResult.amount)}</span>
              </div>
              <div className="flex justify-between gap-2 items-center">
                <span className="text-slate-500">Causale</span>
                <span className="font-bold flex items-center gap-2">
                  {bookingResult.reference}
                  <button
                    onClick={async () => { await navigator.clipboard.writeText(bookingResult.reference); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                    className="text-blue-600 hover:text-blue-800"
                    title="Copia causale"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </span>
              </div>
              {bookingResult.method === "bank" && (
                <>
                  <div className="border-t border-slate-200 my-2" />
                  <div className="flex justify-between gap-2"><span className="text-slate-500">Banca</span><span>{bookingResult.bank_name || "—"}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-slate-500">IBAN</span><span className="font-bold">{bookingResult.iban || "—"}</span></div>
                  {bookingResult.bic_swift && <div className="flex justify-between gap-2"><span className="text-slate-500">BIC/SWIFT</span><span>{bookingResult.bic_swift}</span></div>}
                  {bookingResult.beneficiary && <div className="flex justify-between gap-2"><span className="text-slate-500">Beneficiario</span><span>{bookingResult.beneficiary}</span></div>}
                </>
              )}
            </div>

            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4 text-xs text-blue-900">
              <p className="font-semibold mb-1">⏳ In attesa di conferma</p>
              <p>La transazione resta in stato &quot;in attesa&quot; finché un operatore della tua agenzia non confermerà l&apos;avvenuto incasso.</p>
              <p className="mt-1">Riceverai notifica via email a pagamento confermato.</p>
            </div>

            <div className="flex gap-2 mt-6 justify-end">
              <Button variant="outline" onClick={() => navigate("/area-riservata")}>Chiudi</Button>
              <Button
                data-testid="goto-receipt"
                onClick={() => navigate(`/area-riservata/ricevuta/${bookingResult.tx_id}`)}
                className="bg-[#0F172A] hover:bg-[#1E293B]"
              >
                Vedi ricevuta
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="payment-page">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <button onClick={() => navigate("/area-riservata")} className="text-xs text-slate-300 inline-flex items-center gap-1 hover:text-white" data-testid="back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> Torna alle mie polizze
          </button>
          <button onClick={async () => { await logout(); navigate("/login"); }} className="text-xs text-slate-400 hover:text-white">{user?.email}</button>
        </div>
        <div className="max-w-4xl mx-auto px-6 pb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-300 mb-2">Pagamento polizza</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{data.policy.policy_number}</h1>
          <p className="text-sm text-slate-300 mt-1 capitalize">{data.policy.type} · Scadenza {formatDate(data.policy.end_date)}</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 -mt-6 pb-12 space-y-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Importo da pagare</p>
              <p className="text-4xl font-display font-semibold tabular-nums mt-1" data-testid="pay-amount">{formatCurrency(data.amount_due)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-slate-500">Frazionamento</p>
              <p className="text-sm font-medium capitalize mt-1">{data.policy.frazionamento}</p>
              <p className="text-[11px] text-slate-400 mt-2">Causale: <span className="font-mono">{data.reference}</span></p>
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-display text-xl font-semibold mb-3">Scegli il metodo di pagamento</h2>
          {methods.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900">
              Nessun metodo di pagamento attivo. Contatta la tua agenzia.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="payment-methods">
            {methods.map((m) => {
              const meta = METHOD_META[m];
              const Icon = meta.icon;
              return (
                <button
                  key={m}
                  data-testid={`method-${m}`}
                  onClick={() => initiate(m)}
                  disabled={submitting !== null}
                  className="bg-white border-2 border-slate-200 hover:border-blue-500 hover:shadow-md text-left p-5 rounded-lg transition-all disabled:opacity-50 group"
                >
                  <div className="flex items-center gap-4">
                    <div className={`h-12 w-12 rounded-md bg-${meta.color}-100 text-${meta.color}-700 flex items-center justify-center group-hover:scale-105 transition-transform`}>
                      {submitting === m ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">{meta.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {m === "cash" && "Vieni in agenzia con la ricevuta"}
                        {m === "bank" && "Ti mostriamo IBAN e causale"}
                        {m === "stripe" && "Visa, Mastercard, Amex"}
                        {m === "paypal" && "Conto PayPal o carta"}
                      </p>
                      {/* 🆕 22/07/2026 — Breakdown trasparente commissione Stripe */}
                      {m === "stripe" && data.stripe_breakdown && (
                        <div
                          className="mt-2.5 pt-2.5 border-t border-slate-100 text-[11px] tabular-nums space-y-0.5"
                          data-testid="stripe-fee-breakdown"
                        >
                          <div className="flex justify-between text-slate-500">
                            <span>Polizza</span>
                            <span>{formatCurrency(data.stripe_breakdown.net_amount)}</span>
                          </div>
                          <div className="flex justify-between text-slate-500">
                            <span>Commissione servizio online</span>
                            <span>+ {formatCurrency(data.stripe_breakdown.fee_amount)}</span>
                          </div>
                          <div className="flex justify-between font-bold text-slate-900 pt-1 border-t border-slate-200 mt-1">
                            <span>Totale</span>
                            <span className="text-indigo-700">{formatCurrency(data.stripe_breakdown.gross_amount)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-slate-100 border border-slate-200 rounded-md p-4 text-xs text-slate-600 flex items-start gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p>I pagamenti online sono gestiti tramite gateway certificati (Stripe / PayPal). I tuoi dati non transitano mai sui nostri server. Le ricevute vengono generate automaticamente.</p>
        </div>
      </main>
    </div>
  );
}
